import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { PermissionActions, PermissionModules } from 'types/rolesTypes';
import { ROUTES } from 'routes/routes';
import icons from 'components/darwin-ui/Icon/Icons';
import { ProcessMapTable } from 'types/processesTypes';
import { DarwinButton, DarwinListView } from 'components/darwin-ui';
import useTableControls from 'hooks/useTableControls';
import { useEffect, useState, useCallback } from 'react';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { getProcessMapsColumns } from './columns';
import {
  useChangeStatusProcessMapById,
  useChangeStatusProcessNodesMapById,
  useDuplicateProcessMap,
  useGetProcessMapsByCompany,
} from 'api/services/processMapService';
import { useUI } from 'contexts/UiContext';
import { ErrorType } from 'types/genericTypes';
import { ProcessMapArchiveModal } from 'components/ui/processMap/ProcessMapArchiveModal';
import { ProcessLifeCycleStatus } from 'constants/process-lifecycle-status.constants';

enum ActionKeys {
  EDIT = 'edit',
  VIEW = 'view',
  INACTIVATE = 'inactivate',
  TEST = 'test',
  DEPLOY = 'deploy',
  DUPLICATE = 'duplicate',
  ARCHIVE = 'archive',
  UNARCHIVE = 'unarchive',
}

export enum ProcessMapMode {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export type ColumnAction = {
  key: string;
  name?: keyof typeof icons;
  label?: string;
  onClick?: (processMap: ProcessMapTable) => void;
};

interface ProcessMapListProps {
  mode: ProcessMapMode;
}

const MODE_CONFIGS = {
  [ProcessMapMode.ACTIVE]: {
    titleKey: 'process-map.title',
    showCreateButton: true,
    showArchivedButton: true,
    availableActions: [
      ActionKeys.EDIT,
      ActionKeys.VIEW,
      ActionKeys.INACTIVATE,
      ActionKeys.TEST,
      ActionKeys.DEPLOY,
      ActionKeys.DUPLICATE,
      ActionKeys.ARCHIVE,
    ],
    apiParams: { isArchived: false },
  },
  [ProcessMapMode.ARCHIVED]: {
    titleKey: 'process-map.archived-title',
    showCreateButton: false,
    showArchivedButton: false,
    availableActions: [
      ActionKeys.EDIT,
      ActionKeys.VIEW,
      ActionKeys.INACTIVATE,
      ActionKeys.TEST,
      ActionKeys.DEPLOY,
      ActionKeys.DUPLICATE,
      ActionKeys.UNARCHIVE,
    ],
    apiParams: { isArchived: true },
  },
} as const;

export const ProcessMapList = ({ mode }: ProcessMapListProps) => {
  const config = MODE_CONFIGS[mode];
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const navigate = useNavigate();
  const { openNotification } = useFloatNotification();

  const [processMapToArchive, setProcessMapToArchive] =
    useState<ProcessMapTable>();

  const checkPermission = usePermissionChecker();

  const viewProcessMapPermission = checkPermission(
    PermissionModules.ProcessMaps,
    PermissionActions.Read
  );
  const updateProcessMapPermission = checkPermission(
    PermissionModules.ProcessMaps,
    PermissionActions.Update
  );
  const createProcessMapPermission = checkPermission(
    PermissionModules.ProcessMaps,
    PermissionActions.Create
  );

  // Service for change process map status
  const {
    mutateAsync: changeStatusProcessMap,
    isPending: isPendingChangeStatusProcessMap,
  } = useChangeStatusProcessMapById();

  // Service for change process map nodes status
  const {
    mutateAsync: changeStatusProcessMapNodes,
    isPending: isPendingChangeStatusProcessMapNodes,
  } = useChangeStatusProcessNodesMapById();

  // duplicate process map service
  const {
    mutateAsync: duplicateProcessMap,
    isPending: isDuplicateProcessMapPending,
  } = useDuplicateProcessMap();

  const ChangeStatusProcessMap = async (status: string, processId: string) => {
    try {
      await changeStatusProcessMap({
        payload: { status },
        companyId: selectedCompany || '',
        traceabilityId: processId || '',
      });

      await changeStatusProcessMapNodes({
        payload: { status },
        companyId: selectedCompany || '',
        traceabilityId: processId || '',
      });

      openNotification(
        t('success'),
        t('process-map.update-status.success-notification-message', {
          active: status,
        }),
        'success'
      );
    } catch (error) {
      const apiError = error as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('process-map.update-status.error-notification-message', {
            active: status,
          }),
          'error'
        );
      }
    }
  };

  const handleDuplicateProcessMap = async (processId: string) => {
    try {
      await duplicateProcessMap({
        companyId: selectedCompany || '',
        traceabilityPlanId: processId || '',
      });

      openNotification(
        t('success'),
        t('process-map.duplicate-process-map-success'),
        'success'
      );
    } catch (error) {
      const apiError = error as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('process-map.duplicate-process-map-error'),
          'error'
        );
      }
    }
  };

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls();

  const {
    data: processMapData,
    isLoading: isLoadingProcessMapData,
    isFetching: isFetchingProcessMapData,
    isPending: isPendingProcessMapData,
    error: processMapsError,
  } = useGetProcessMapsByCompany({
    companyId: selectedCompany || '',
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
    ...config.apiParams,
  });

  const [archivedCount, setArchivedCount] = useState(0);

  useEffect(() => {
    if (processMapData?.metadata?.archivedCount !== undefined) {
      setArchivedCount(processMapData.metadata.archivedCount);
    }
  }, [processMapData?.metadata?.archivedCount]);

  useEffect(() => {
    if (processMapsError?.message) {
      openNotification(
        'Error',
        t('process-map.process-error-message'),
        'error'
      );
    }
  }, [processMapsError?.message, openNotification, t]);

  const tableData = processMapData?.data ?? [];
  const actionDefinitions: Record<string, ColumnAction> = {
    [ActionKeys.EDIT]: {
      key: ActionKeys.EDIT,
      name: 'edit',
      label: t('list-view.action-items.edit'),
      onClick: process => handleConfigProcessMap(process),
    },
    [ActionKeys.VIEW]: {
      key: ActionKeys.VIEW,
      name: 'view',
      label: t('list-view.action-items.view'),
      onClick: process =>
        navigate(
          generatePath(ROUTES.PROCESSES.MAPS.MAP.VIEW, { id: process.id })
        ),
    },
    [ActionKeys.INACTIVATE]: {
      key: ActionKeys.INACTIVATE,
      name: 'inactivate',
      label: t('list-view.action-items.inactive'),
      onClick: process => {
        if (process.status === ProcessLifeCycleStatus.INACTIVE) {
          return openNotification(
            t('info'),
            t('process-map.update-status.can-not-update', {
              status: ProcessLifeCycleStatus.INACTIVE,
            }),
            'info',
            'alert'
          );
        }
        return ChangeStatusProcessMap(
          ProcessLifeCycleStatus.INACTIVE,
          process.id
        );
      },
    },
    [ActionKeys.TEST]: {
      key: ActionKeys.TEST,
      name: 'test',
      label: t('list-view.action-items.test'),
      onClick: process => {
        if (process.status === ProcessLifeCycleStatus.TEST) {
          return openNotification(
            t('info'),
            t('process-map.update-status.can-not-update', {
              status: ProcessLifeCycleStatus.TEST,
            }),
            'info',
            'alert'
          );
        }
        return ChangeStatusProcessMap(ProcessLifeCycleStatus.TEST, process.id);
      },
    },
    [ActionKeys.DEPLOY]: {
      key: ActionKeys.DEPLOY,
      name: 'deploy',
      label: t('list-view.action-items.deploy'),
      onClick: process => {
        if (process.status === ProcessLifeCycleStatus.PRODUCTION) {
          return openNotification(
            t('info'),
            t('process-map.update-status.can-not-update', {
              status: ProcessLifeCycleStatus.PRODUCTION,
            }),
            'info',
            'alert'
          );
        }
        return ChangeStatusProcessMap(
          ProcessLifeCycleStatus.PRODUCTION,
          process.id
        );
      },
    },
    [ActionKeys.DUPLICATE]: {
      key: ActionKeys.DUPLICATE,
      name: 'duplicate',
      label: t('list-view.action-items.duplicate'),
      onClick: process => handleDuplicateProcessMap(process.id),
    },
    [ActionKeys.ARCHIVE]: {
      key: ActionKeys.ARCHIVE,
      name: 'archive',
      label: t('list-view.action-items.archive'),
      onClick: setProcessMapToArchive,
    },
    [ActionKeys.UNARCHIVE]: {
      key: ActionKeys.UNARCHIVE,
      name: 'archive',
      label: t('list-view.action-items.unarchive'),
      onClick: setProcessMapToArchive,
    },
  };

  const permissionMap: Record<string, boolean | null> = {
    [ActionKeys.EDIT]: updateProcessMapPermission,
    [ActionKeys.VIEW]: viewProcessMapPermission,
    [ActionKeys.INACTIVATE]: updateProcessMapPermission,
    [ActionKeys.TEST]: updateProcessMapPermission,
    [ActionKeys.DEPLOY]: updateProcessMapPermission,
    [ActionKeys.DUPLICATE]: createProcessMapPermission,
    [ActionKeys.ARCHIVE]: updateProcessMapPermission,
    [ActionKeys.UNARCHIVE]: updateProcessMapPermission,
  };

  const actions: ColumnAction[] = config.availableActions
    .filter(actionKey => permissionMap[actionKey])
    .map(actionKey => actionDefinitions[actionKey]);

  const columns = getProcessMapsColumns(
    t,
    actions,
    searchQuery,
    config.apiParams.isArchived,
    undefined,
    undefined
  );

  const isLoading =
    isLoadingProcessMapData ||
    isFetchingProcessMapData ||
    isPendingProcessMapData ||
    isPendingChangeStatusProcessMap ||
    isPendingChangeStatusProcessMapNodes ||
    isDuplicateProcessMapPending ||
    viewProcessMapPermission === null;

  const createNewProcessMap = () => {
    navigate(generatePath(ROUTES.PROCESSES.MAPS.CREATE));
  };

  const handleConfigProcessMap = (process: ProcessMapTable) => {
    navigate(
      generatePath(ROUTES.PROCESSES.MAPS.MAP.CONFIG, {
        id: process.id,
        list: '1',
      })
    );
  };

  const handleRowClick = useCallback(
    (processMap: ProcessMapTable) => {
      if (viewProcessMapPermission) {
        navigate(
          generatePath(ROUTES.PROCESSES.MAPS.MAP.EDIT, {
            id: processMap.id,
          })
        );
      }
    },
    [viewProcessMapPermission, navigate]
  );

  const handleBack = () => {
    navigate(generatePath(ROUTES.PROCESSES.MAPS.LIST));
  };

  return (
    <>
      <ContentHeader
        title={t(config.titleKey)}
        {...(config.showCreateButton &&
          createProcessMapPermission && {
            onSave: createNewProcessMap,
            onSaveTitle: t('process-map.new-process-map.title'),
          })}
        displayExtraButton={true}
        extraButtonTitle={
          mode === ProcessMapMode.ARCHIVED ? t('common.back') : undefined
        }
        onExtraButtonClick={
          mode === ProcessMapMode.ARCHIVED ? handleBack : undefined
        }
      />

      <DarwinListView<ProcessMapTable>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        pagination={{
          total: processMapData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
        header={{
          setSearchQuery,
          searchPlaceholder: t('process-map.process-search-placeholder'),
          actions: config.showArchivedButton ? (
            <DarwinButton
              onClick={() =>
                navigate(generatePath(ROUTES.PROCESSES.MAPS.ARCHIVED))
              }
            >{`${t('process-map.archived-button')} ${
              archivedCount > 0 ? `(${archivedCount})` : ''
            }`}</DarwinButton>
          ) : undefined,
        }}
        table={{
          onChange: onTableChange,
        }}
        behavior={{
          isRowInactive: processMap =>
            processMap.status === ProcessLifeCycleStatus.INACTIVE,
          onRowClick: handleRowClick,
        }}
      />

      <ProcessMapArchiveModal
        open={!!processMapToArchive}
        onCancel={() => setProcessMapToArchive(undefined)}
        processMapId={processMapToArchive?.id.toString()}
        name={processMapToArchive?.name}
        isArchive={mode === ProcessMapMode.ACTIVE}
      />
    </>
  );
};
